import { Puzzle } from "lucide-react";
import { cn } from "@wa/ui";
import { GAP_CLASS, type ComponentFreshness, type WpDetails, type WpFreshness } from "../../../../lib/siteScan";
import { StatusPill } from "../../../ui";
import type { T } from "../types";
import { Field, Panel } from "./primitives";

/**
 * The WordPress inventory, as a maintenance report rather than a list of slugs.
 *
 * Sorted worst first: plugins with no upstream release in years, then the ones
 * behind a major, then the merely outdated. That order is the order the
 * conversation happens in, and it means the operator never scrolls to find the
 * argument.
 *
 * This stays a `<table>` while the rest of the CRM moved to pill rows, and that
 * is a decision rather than an oversight. The redesign's rule is that a pill is
 * a list of objects you click and a table is numbers you compare - and the two
 * middle columns here are one number read twice, running against installed. A
 * plugin row opens nothing: there is no plugin page to go to, so the row has no
 * subject and nothing to put in a 44px menu. Reading "5.4.2" against "6.7.1"
 * down a column is the whole point of the panel, and pills would break the
 * alignment that makes it possible.
 */

/** One plugin row: slug, running version, current version and its age. */
function PluginRow({ item, t }: { item: ComponentFreshness; t: T }) {
  const stale = item.gap === "minor" || item.gap === "major";
  return (
    <tr className="border-b border-[var(--crm-hair)] last:border-0">
      <td className="py-1.5 pr-3">
        <span className="font-mono text-xs">{item.slug}</span>
        {item.name !== item.slug && <span className="ml-1.5 text-[11px] text-muted-foreground">{item.name}</span>}
      </td>
      <td className="py-1.5 pr-3 text-right">
        <span className={cn("font-mono text-xs", GAP_CLASS[item.gap])}>{item.current ?? "?"}</span>
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-xs text-muted-foreground">
        {item.unlisted ? t("scanner.freshness.premiumShort") : (item.latest ?? "-")}
      </td>
      <td className="py-1.5 text-right">
        {/* Abandoned is late, behind is waiting, current says nothing at all.
            These were two hand-written tinted spans - the last ad-hoc palette
            left in the scanner after everything around them moved to tones. */}
        {item.abandoned ? (
          <StatusPill tone="lt">{t("scanner.freshness.years", { count: item.ageYears ?? 2 })}</StatusPill>
        ) : stale ? (
          <StatusPill tone="wt">{t(`scanner.gap.${item.gap}`)}</StatusPill>
        ) : null}
      </td>
    </tr>
  );
}

export function WordPressPanel({
  wp,
  freshness,
  t,
}: {
  wp: WpDetails;
  freshness: WpFreshness | null;
  t: T;
}) {
  // The freshness list is authoritative when it exists: it carries the same
  // plugins with the vendor's numbers attached. Without it (no network, or the
  // reference has never loaded) the plain inventory is still shown.
  const rows: ComponentFreshness[] =
    freshness?.plugins ??
    wp.plugins.map((p) => ({
      slug: p.slug,
      name: p.slug,
      current: p.version,
      latest: null,
      gap: "unknown" as const,
      releasedAt: null,
      ageYears: null,
      abandoned: false,
      unlisted: false,
      activeInstalls: null,
    }));

  const sorted = [...rows].sort((a, b) => {
    const rank = (x: ComponentFreshness) =>
      x.abandoned ? 0 : x.gap === "major" ? 1 : x.gap === "minor" ? 2 : x.gap === "patch" ? 3 : 4;
    const diff = rank(a) - rank(b);
    return diff !== 0 ? diff : a.slug.localeCompare(b.slug);
  });

  return (
    <Panel
      icon={Puzzle}
      title={t("scanner.wp.title")}
      aside={
        wp.version ? (
          <StatusPill tone={freshness?.core.status === "insecure" ? "lt" : "mut"} className="font-mono">
            {t("scanner.wp.core")} {wp.version}
            {freshness?.core.latest ? ` / ${freshness.core.latest}` : ""}
          </StatusPill>
        ) : undefined
      }
    >
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label={t("scanner.wp.theme")} value={wp.theme ? wp.theme.name : "-"} mono />
        <Field label={t("scanner.wp.themeVersion")} value={wp.theme?.version ?? "-"} mono />
        <Field label={t("scanner.wp.rest")} value={t(wp.restApi ? "common.yes" : "common.no")} />
        <Field label={t("scanner.wp.xmlrpc")} value={t(wp.xmlrpc ? "common.yes" : "common.no")} />
      </dl>

      {(wp.siteName || wp.siteDescription) && (
        <dl className="mt-3 grid gap-3 border-t border-[var(--crm-hair)] pt-3 sm:grid-cols-2">
          <Field label={t("scanner.wp.siteName")} value={wp.siteName} />
          <Field label={t("scanner.wp.tagline")} value={wp.siteDescription} />
        </dl>
      )}

      <div className="mt-3 border-t border-[var(--crm-hair)] pt-3">
        <p className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-muted-foreground">
          {t("scanner.wp.plugins", { count: wp.plugins.length })}
        </p>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("scanner.wp.noPlugins")}</p>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full min-w-[420px]">
              <thead>
                <tr className="border-b border-[var(--crm-hair)] text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-muted-foreground">
                  <th className="py-1 pr-3 text-left font-[inherit]">{t("scanner.wp.plugin")}</th>
                  <th className="py-1 pr-3 text-right font-[inherit]">{t("scanner.wp.installed")}</th>
                  <th className="py-1 pr-3 text-right font-[inherit]">{t("scanner.wp.latest")}</th>
                  <th className="py-1 text-right font-[inherit]">{t("scanner.wp.state")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <PluginRow key={p.slug} item={p} t={t} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
